import { Building2, Lock, Eye, EyeOff, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";

export function BeneficiaryCard({ 
  beneficiary, 
  showFullDetails = false, 
  onSelect,
  selected = false,
  compact = false,
  isAus = false
}) {
  const [showSensitive, setShowSensitive] = useState(false);

  if (!beneficiary) return null;

  const aus = isAus || beneficiary.currency === "AUD" || ["AUS", "AU"].includes((beneficiary.country || "").toUpperCase());
  const sortLabel = aus ? "BSB" : "Sort Code";
  const fmtSort = (v) => {
    const d = String(v || "").replace(/\D/g, "");
    if (d.length !== 6) return v;
    return aus ? `${d.slice(0, 3)}-${d.slice(3)}` : `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
  };
  const rawAcct = beneficiary.full_account_number || beneficiary.account_number;
  const rawSort = beneficiary.full_sort_code || beneficiary.sort_code;
  const maskedAcct = beneficiary.masked_account
    || (rawAcct ? "••••" + String(rawAcct).slice(-4) : "");
  const maskedSort = beneficiary.masked_sort_code
    || (rawSort ? (aus ? "•••-•" : "••-••-") + String(rawSort).replace(/\D/g, "").slice(-2) : "");

  const displayAccount = showFullDetails && showSensitive && rawAcct
    ? rawAcct
    : maskedAcct;

  const displaySortCode = showFullDetails && showSensitive && rawSort
    ? fmtSort(rawSort)
    : maskedSort;

  if (compact) {
    return (
      <div 
        className={cn(
          "p-3 rounded-lg border cursor-pointer transition-all",
          selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        )}
        onClick={onSelect}
        data-testid={`beneficiary-card-compact-${beneficiary.beneficiary_id}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">{beneficiary.bank_name || "Bank Account"}</p>
              <p className="text-xs text-muted-foreground font-mono">
                {maskedAcct} • {maskedSort}
              </p>
            </div>
          </div>
          <StatusBadge status={beneficiary.verification_status} size="sm" />
        </div>
      </div>
    );
  }

  return (
    <Card 
      className={cn(
        "card-hover",
        selected && "ring-2 ring-primary"
      )}
      data-testid={`beneficiary-card-${beneficiary.beneficiary_id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{beneficiary.bank_name || "Bank Account"}</CardTitle>
              <p className="text-sm text-muted-foreground">{beneficiary.company_name}</p>
            </div>
          </div>
          <StatusBadge status={beneficiary.verification_status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Account Number</p>
            <div className="flex items-center gap-2">
              <Lock className="w-3 h-3 text-muted-foreground" />
              <span className="font-mono text-sm masked-text" data-testid="account-number">
                {displayAccount}
              </span>
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{sortLabel}</p>
            <div className="flex items-center gap-2">
              <Lock className="w-3 h-3 text-muted-foreground" />
              <span className="font-mono text-sm masked-text" data-testid="sort-code">
                {displaySortCode}
              </span>
            </div>
          </div>
        </div>

        {showFullDetails && (beneficiary.full_account_number || beneficiary.full_sort_code) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSensitive(!showSensitive)}
            className="w-full"
            data-testid="toggle-sensitive-btn"
          >
            {showSensitive ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Full Details
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Full Details
              </>
            )}
          </Button>
        )}

        {onSelect && (
          <Button
            variant={selected ? "secondary" : "default"}
            size="sm"
            className="w-full"
            onClick={onSelect}
            data-testid="select-beneficiary-btn"
          >
            {selected ? "Selected" : "Select This Beneficiary"}
          </Button>
        )}

        <div className="text-xs text-muted-foreground pt-2 border-t">
          {beneficiary.verified_date && (
            <p>Verified: {new Date(beneficiary.verified_date).toLocaleDateString("en-GB")}</p>
          )}
          {beneficiary.verified_by && (
            <p>By: {beneficiary.verified_by}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function MaskedField({ label, value, icon: Icon = Lock }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <span className="font-mono masked-text">{value}</span>
      </div>
    </div>
  );
}
